import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';

export default function AudienceCreator() {
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');
  const [customerId, setCustomerId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [members, setMembers] = useState([
    { memberType: 'KEYWORD', parameter: '' }
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingConn, setLoadingConn] = useState(false);

  const brandId = useMemo(() => {
    try {
      const current = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId');
      if (current) return current;
    } catch (_) {}
    return '';
  }, []);

  useEffect(() => {
    const autoFill = async () => {
      try {
        setLoadingConn(true);
        if (!brandId) return;
        const resp = await fetch('https://api.glowytics.com/api/auth/connections', {
          headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
          credentials: 'include',
        });
        if (!resp.ok) return;
        const data = await resp.json();
        let found = null;
        if (Array.isArray(data)) found = data.find(x => x?.platform === 'GOOGLE_ADS');
        if (found?.customerId) setCustomerId(String(found.customerId));
      } catch (_) {
        // ignore
      } finally {
        setLoadingConn(false);
      }
    };
    autoFill();
  }, [brandId]);

  const setMemberField = (idx, field, value) => {
    setMembers(prev => {
      const copy = prev.slice();
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const addMember = () => {
    setMembers(prev => [...prev, { memberType: 'KEYWORD', parameter: '' }]);
  };

  const removeMember = (idx) => {
    setMembers(prev => prev.filter((_, i) => i !== idx));
  };

  const focusLastParamInput = () => {
    try {
      const nodes = document.querySelectorAll('[data-testid="ca-param-input"]');
      if (nodes && nodes.length) {
        const el = nodes[nodes.length - 1];
        if (el && typeof el.focus === 'function') el.focus();
      }
    } catch (_) {}
  };

  const handleParamKeyDown = (idx, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const val = String(members[idx]?.parameter || '').trim();
      if (!val) return;
      // Accept current member and add a new empty row
      addMember();
      setTimeout(focusLastParamInput, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!brandId) { toast.error('Select a brand first'); return; }
      if (!name.trim()) { toast.error('Please enter a name'); return; }
      const valid = members.filter(m => (m.parameter || '').trim().length > 0);
      if (valid.length === 0) { toast.error('Add at least one member'); return; }
      setSubmitting(true);
      const body = {
        customerId: (customerId || '').trim(),
        name: name.trim(),
        description: description || '',
        members: valid.map(m => ({ memberType: m.memberType, parameter: m.parameter.trim() }))
      };
      const resp = await fetch('https://api.glowytics.com/api/google-ads/audiences/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      const txt = await resp.text(); let js; try { js = JSON.parse(txt); } catch { js = { raw: txt }; }
      if (!resp.ok || js?.success === false) {
        throw new Error(js?.error || resp.statusText || 'Failed to create audience');
      }
      toast.success('Custom audience created');
      // Clear members but keep name/description
      setMembers([{ memberType: 'KEYWORD', parameter: '' }]);
    } catch (err) {
      toast.error(err?.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Create Custom Audience (Google Ads)</h1>
          <p className="text-sm text-gray-500">Add KEYWORD, URL or APP members and submit to Google Ads.</p>
        </div>

        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            // Prevent Enter from submitting the whole form while typing
            if (e.key === 'Enter' && e.target && e.target.tagName === 'INPUT') {
              e.preventDefault();
            }
          }}
          className="bg-white rounded-2xl border shadow-sm p-5 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Customer ID</label>
              <input
                value={customerId}
                onChange={(e)=>setCustomerId(e.target.value)}
                placeholder="e.g. 123-456-7890"
                className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
              />
              {loadingConn && <div className="text-[11px] text-gray-500 mt-1">Loading connection…</div>}
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Audience Name</label>
              <input
                value={name}
                onChange={(e)=>setName(e.target.value)}
                placeholder="Glowy - Custom Audience"
                className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-500 mb-1">Description (optional)</label>
            <input
              value={description}
              onChange={(e)=>setDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-800">Members</div>
              <button type="button" onClick={addMember} className="px-3 py-1.5 text-xs font-medium rounded bg-gray-900 text-white hover:bg-gray-800">
                + Add Member
              </button>
            </div>
            <div className="space-y-2">
              {members.map((m, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border rounded-lg p-3">
                  <div className="md:col-span-3">
                    <label className="block text-[11px] text-gray-500 mb-1">Type</label>
                    <select
                      value={m.memberType}
                      onChange={(e)=>setMemberField(idx, 'memberType', e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg"
                    >
                      <option value="KEYWORD">KEYWORD</option>
                      <option value="URL">URL</option>
                      <option value="APP">APP</option>
                      <option value="PLACE_CATEGORY">PLACE_CATEGORY</option>
                    </select>
                  </div>
                  <div className="md:col-span-8">
                    <label className="block text-[11px] text-gray-500 mb-1">Parameter</label>
                    <input
                      data-testid="ca-param-input"
                      value={m.parameter}
                      onChange={(e)=>setMemberField(idx, 'parameter', e.target.value)}
                      onKeyDown={(e)=>handleParamKeyDown(idx, e)}
                      placeholder={m.memberType === 'APP' ? 'com.example.android.app' : (m.memberType === 'URL' ? 'https://example.com/page' : 'keyword')}
                      className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                    />
                  </div>
                  <div className="md:col-span-1 flex items-end">
                    <button type="button" onClick={()=>removeMember(idx)} className="px-3 py-2 text-xs font-medium rounded bg-red-50 text-red-600 hover:bg-red-100">
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${submitting ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            >
              {submitting ? 'Creating…' : 'Create Custom Audience'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


